import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  FileText, Download, Mail, Presentation, BookOpen, Copy, Check, 
  FileDown, Briefcase, Users, TrendingUp, Sparkles, Palette, Zap,
  Eye, Upload, Folder, FolderPlus, Search, Trash2, Edit, Video,
  Image, FileArchive, File, MoreVertical, RefreshCw, X, Plus,
  Share2, Filter, History
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

// File type icons mapping
const fileTypeIcons = {
  document: FileText,
  image: Image,
  video: Video,
  archive: FileArchive,
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function AgentMarketing() {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [downloading, setDownloading] = useState({});
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  
  // Resources state
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editResource, setEditResource] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: "",
    description: "",
    category_id: "",
    file: null
  });
  const [newCategory, setNewCategory] = useState({ name: "", description: "", color: "#4A7C59" });
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedResourceVersions, setSelectedResourceVersions] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [newVersionFile, setNewVersionFile] = useState(null);
  const fileInputRef = useRef(null);
  const versionFileRef = useRef(null);

  useEffect(() => {
    fetchAssets();
    fetchEmailTemplates();
    fetchCategories();
    fetchResources();
  }, []);

  const fetchAssets = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/assets/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      toast.error("Failed to load marketing assets");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data);
    } catch (error) {
      console.error("Failed to load email templates");
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const fetchResources = async (categoryFilter = null, searchFilter = null) => {
    setResourcesLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== "all") params.append("category_id", categoryFilter);
      if (searchFilter) params.append("search", searchFilter);
      
      const response = await axios.get(`${API}/marketing/resources?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data.resources || []);
    } catch (error) {
      console.error("Failed to load resources");
    } finally {
      setResourcesLoading(false);
    }
  };

  const handleDownload = async (type, style = null) => {
    const key = style ? `${type}_${style}` : type;
    setDownloading(prev => ({ ...prev, [key]: true }));
    const token = localStorage.getItem("agent_token");
    
    try {
      const url = style 
        ? `${API}/marketing/brochure/${style}/download`
        : `${API}/marketing/pitch-deck/download`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const filename = style 
        ? `TheRentPayer_Brochure_${style}.pdf`
        : 'TheRentPayer_Pitch_Deck.pptx';
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePreviewTemplate = async (templateName) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/email-preview/${encodeURIComponent(templateName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewTemplate(response.data);
    } catch (error) {
      toast.error("Failed to load template preview");
    }
  };

  const copyToClipboard = (text, templateName) => {
    navigator.clipboard.writeText(text);
    setCopiedTemplate(templateName);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  // Resource upload handler
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 50 MB");
        return;
      }
      setUploadForm(prev => ({ 
        ...prev, 
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.name || !uploadForm.category_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);
    const token = localStorage.getItem("agent_token");
    
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("name", uploadForm.name);
      formData.append("description", uploadForm.description || "");
      formData.append("category_id", uploadForm.category_id);

      await axios.post(`${API}/marketing/resources/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Resource uploaded successfully");
      setUploadDialogOpen(false);
      setUploadForm({ name: "", description: "", category_id: "", file: null });
      fetchResources(selectedCategory !== "all" ? selectedCategory : null, searchQuery || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadResource = async (resource) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/resources/${resource.resource_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = resource.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${resource.name}`);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!confirm("Are you sure you want to delete this resource?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/marketing/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resource deleted");
      fetchResources(selectedCategory !== "all" ? selectedCategory : null, searchQuery || null);
    } catch (error) {
      toast.error("Failed to delete resource");
    }
  };

  const handleViewVersions = async (resource) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/resources/${resource.resource_id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedResourceVersions({ ...response.data, resource });
      setVersionDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load version history");
    }
  };

  const handleUploadNewVersion = async () => {
    if (!newVersionFile || !selectedResourceVersions) return;
    
    setUploadingVersion(true);
    const token = localStorage.getItem("agent_token");
    
    try {
      const formData = new FormData();
      formData.append("file", newVersionFile);

      await axios.post(`${API}/marketing/resources/${selectedResourceVersions.resource_id}/version`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("New version uploaded successfully");
      setNewVersionFile(null);
      
      // Refresh versions
      const response = await axios.get(`${API}/marketing/resources/${selectedResourceVersions.resource_id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedResourceVersions({ ...response.data, resource: selectedResourceVersions.resource });
      
      // Refresh resources list
      fetchResources(selectedCategory !== "all" ? selectedCategory : null, searchQuery || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload new version");
    } finally {
      setUploadingVersion(false);
    }
  };

  const handleDownloadVersion = async (resourceId, version, filename) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/marketing/resources/${resourceId}/versions/${version}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded version ${version}`);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      toast.error("Category name is required");
      return;
    }
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/marketing/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Category created");
      setCategoryDialogOpen(false);
      setNewCategory({ name: "", description: "", color: "#4A7C59" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create category");
    }
  };

  const handleSearch = () => {
    fetchResources(selectedCategory !== "all" ? selectedCategory : null, searchQuery || null);
  };

  const handleCategoryFilter = (catId) => {
    setSelectedCategory(catId);
    fetchResources(catId !== "all" ? catId : null, searchQuery || null);
  };

  const categoryIcons = {
    sales_outreach: Briefcase,
    onboarding: Users,
    reengagement: TrendingUp,
    transactional: Mail,
  };

  const categoryLabels = {
    sales_outreach: "Sales & Outreach",
    onboarding: "Onboarding",
    reengagement: "Re-engagement",
    transactional: "Transactional",
  };

  const brochureStyles = {
    professional: { icon: Sparkles, color: "text-blue-600", bg: "bg-blue-50" },
    modern: { icon: Palette, color: "text-orange-600", bg: "bg-orange-50" },
    bold: { icon: Zap, color: "text-purple-600", bg: "bg-purple-50" },
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          <BookOpen className="inline h-8 w-8 mr-2 text-primary" />
          Marketing Assets
        </h1>
        <p className="text-muted-foreground mt-1">
          Download pitch decks, brochures, email templates, and manage resources
        </p>
      </div>

      <Tabs defaultValue="downloads" className="space-y-6">
        <TabsList>
          <TabsTrigger value="downloads" data-testid="downloads-tab">
            <Download className="h-4 w-4 mr-2" />
            Downloads
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="resources-tab">
            <Folder className="h-4 w-4 mr-2" />
            Resources
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="emails-tab">
            <Mail className="h-4 w-4 mr-2" />
            Email Templates
          </TabsTrigger>
        </TabsList>

        {/* Downloads Tab */}
        <TabsContent value="downloads" className="space-y-6">
          {/* Pitch Deck Section */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Presentation className="inline h-5 w-5 mr-2 text-primary" />
                Pitch Deck
              </CardTitle>
              <CardDescription>
                10-slide investor presentation covering problem, solution, market, and business model
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-lg bg-primary/20 flex items-center justify-center">
                    <FileText className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">TheRentPayer Investor Pitch Deck</h3>
                    <p className="text-sm text-muted-foreground">PowerPoint (.pptx) - 10 slides</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">Investors</Badge>
                      <Badge variant="outline">Partners</Badge>
                      <Badge variant="outline">Customers</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => handleDownload('pitch-deck')}
                  disabled={downloading['pitch-deck']}
                  size="lg"
                  data-testid="download-pitchdeck-btn"
                >
                  {downloading['pitch-deck'] ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download PPTX
                </Button>
              </div>

              {/* Slide Preview */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Slide Overview</h4>
                <div className="grid grid-cols-5 gap-3">
                  {[
                    "Title & Intro",
                    "The Problem",
                    "Our Solution",
                    "How It Works",
                    "Market Size",
                    "Business Model",
                    "Competitive Edge",
                    "Traction",
                    "Team",
                    "The Ask"
                  ].map((slide, i) => (
                    <div key={i} className="p-3 bg-muted rounded-lg text-center">
                      <div className="text-xs text-muted-foreground mb-1">Slide {i + 1}</div>
                      <div className="text-sm font-medium">{slide}</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Brochures Section */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <FileDown className="inline h-5 w-5 mr-2 text-primary" />
                Trifold Brochures
              </CardTitle>
              <CardDescription>
                Print-ready PDF brochures in three distinct styles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {assets?.brochures?.map((brochure) => {
                  const styleConfig = brochureStyles[brochure.style];
                  const StyleIcon = styleConfig?.icon || FileText;
                  
                  return (
                    <div 
                      key={brochure.style}
                      className={`p-5 rounded-xl border-2 transition-all hover:shadow-md ${styleConfig?.bg || 'bg-muted'}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${styleConfig?.color || 'text-gray-600'} bg-white`}>
                          <StyleIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{brochure.name}</h3>
                          <p className="text-xs text-muted-foreground">PDF - Trifold</p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {brochure.description}
                      </p>
                      <Button 
                        onClick={() => handleDownload('brochure', brochure.style)}
                        disabled={downloading[`brochure_${brochure.style}`]}
                        variant="outline"
                        className="w-full"
                        data-testid={`download-brochure-${brochure.style}-btn`}
                      >
                        {downloading[`brochure_${brochure.style}`] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        Download PDF
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Market Stats Card */}
          <Card className="card-organic bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <TrendingUp className="inline h-5 w-5 mr-2 text-primary" />
                Key Market Statistics
              </CardTitle>
              <CardDescription>
                Use these in your presentations and outreach
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { label: "US Renter Households", value: "44M", desc: "Total addressable market" },
                  { label: "Monthly Job Losses", value: "150K+", desc: "In economic downturns" },
                  { label: "Renters Without Savings", value: "67%", desc: "Less than $1,000 saved" },
                  { label: "Annual Eviction Filings", value: "3.6M", desc: "Across the United States" },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-3xl font-bold text-primary">{stat.value}</div>
                    <div className="font-medium text-sm mt-1">{stat.label}</div>
                    <div className="text-xs text-muted-foreground">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="space-y-6">
          {/* Actions Bar */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2 items-center flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                  data-testid="resource-search-input"
                />
              </div>
              <Button variant="outline" onClick={handleSearch} data-testid="search-btn">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="add-category-btn">
                <FolderPlus className="h-4 w-4 mr-2" />
                New Category
              </Button>
              <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-resource-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload Resource
              </Button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryFilter("all")}
              data-testid="filter-all-btn"
            >
              <Folder className="h-4 w-4 mr-1" />
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.category_id}
                variant={selectedCategory === cat.category_id ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryFilter(cat.category_id)}
                style={{ borderColor: selectedCategory === cat.category_id ? cat.color : undefined }}
                data-testid={`filter-${cat.category_id}-btn`}
              >
                <span className="h-3 w-3 rounded-full mr-2" style={{ backgroundColor: cat.color }} />
                {cat.name}
              </Button>
            ))}
          </div>

          {/* Resources Grid */}
          {resourcesLoading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : resources.length === 0 ? (
            <Card className="card-organic">
              <CardContent className="py-12 text-center">
                <Folder className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg mb-2">No resources found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? "Try a different search term" : "Upload your first marketing resource"}
                </p>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Resource
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((resource) => {
                const FileIcon = fileTypeIcons[resource.file_type] || File;
                const category = categories.find(c => c.category_id === resource.category_id);
                
                return (
                  <Card key={resource.resource_id} className="card-organic hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                            <FileIcon className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate" title={resource.name}>{resource.name}</h4>
                            <p className="text-xs text-muted-foreground">
                              {resource.file_extension.toUpperCase()} - {formatFileSize(resource.file_size)}
                              {resource.current_version > 1 && (
                                <span className="ml-2 text-primary">v{resource.current_version}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {resource.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{resource.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <Badge 
                          variant="outline" 
                          style={{ borderColor: category?.color, color: category?.color }}
                        >
                          {resource.category_name}
                        </Badge>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewVersions(resource)}
                            title="Version history"
                            data-testid={`versions-resource-${resource.resource_id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadResource(resource)}
                            data-testid={`download-resource-${resource.resource_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteResource(resource.resource_id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-resource-${resource.resource_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t text-xs text-muted-foreground flex justify-between">
                        <span>By {resource.uploaded_by_name}</span>
                        <span>{resource.download_count} downloads</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="emails" className="space-y-6">
          <div className="grid gap-6">
            {Object.entries(emailTemplates).map(([category, templates]) => {
              const CategoryIcon = categoryIcons[category] || Mail;
              
              return (
                <Card key={category} className="card-organic">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                      <CategoryIcon className="inline h-5 w-5 mr-2 text-primary" />
                      {categoryLabels[category] || category}
                    </CardTitle>
                    <CardDescription>
                      {templates.length} template{templates.length !== 1 ? 's' : ''} available
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {templates.map((template, i) => (
                        <div 
                          key={i}
                          className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                        >
                          <div className="flex-1">
                            <h4 className="font-medium">{template.name}</h4>
                            <p className="text-sm text-muted-foreground">{template.subject}</p>
                            <Badge variant="outline" className="mt-2">{template.category}</Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreviewTemplate(template.name)}
                              data-testid={`preview-template-${i}-btn`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(template.body, template.name)}
                              data-testid={`copy-template-${i}-btn`}
                            >
                              {copiedTemplate === template.name ? (
                                <Check className="h-4 w-4 mr-1 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4 mr-1" />
                              )}
                              Copy
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Template Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              <Badge className="mt-2">{previewTemplate?.category}</Badge>
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Subject Line</label>
                <div className="p-3 bg-muted rounded-lg mt-1 font-medium">
                  {previewTemplate?.subject}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email Body</label>
                <div className="p-4 bg-muted rounded-lg mt-1 whitespace-pre-wrap font-mono text-sm">
                  {previewTemplate?.body}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => copyToClipboard(previewTemplate?.body, 'preview')}
                  className="flex-1"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Body
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(`Subject: ${previewTemplate?.subject}\n\n${previewTemplate?.body}`, 'full')}
                  className="flex-1"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy All
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Upload Resource Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Marketing Resource</DialogTitle>
            <DialogDescription>
              Upload PowerPoints, PDFs, images, videos, or other marketing materials
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File Drop Zone */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${uploadForm.file ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.zip,.rar"
              />
              {uploadForm.file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{uploadForm.file.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(uploadForm.file.size)}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadForm(prev => ({ ...prev, file: null }));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="font-medium">Click to select a file</p>
                  <p className="text-sm text-muted-foreground">Max 50 MB - PDF, PPTX, DOCX, Images, Videos, ZIP</p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="resource-name">Name *</Label>
              <Input
                id="resource-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Resource name"
                data-testid="resource-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resource-category">Category *</Label>
              <Select 
                value={uploadForm.category_id} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, category_id: v }))}
              >
                <SelectTrigger data-testid="resource-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="resource-description">Description</Label>
              <Textarea
                id="resource-description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this resource"
                rows={3}
                data-testid="resource-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="submit-upload-btn">
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Category</DialogTitle>
            <DialogDescription>
              Organize your marketing resources into categories
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">Name *</Label>
              <Input
                id="category-name"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Input
                id="category-description"
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category-color">Color</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  id="category-color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  className="h-10 w-16 rounded border cursor-pointer"
                />
                <Input
                  value={newCategory.color}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} data-testid="submit-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <History className="inline h-5 w-5 mr-2" />
              Version History
            </DialogTitle>
            <DialogDescription>
              {selectedResourceVersions?.name} - {selectedResourceVersions?.versions?.length || 0} version(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload New Version */}
            <div className="border-2 border-dashed rounded-lg p-4">
              <input
                ref={versionFileRef}
                type="file"
                className="hidden"
                onChange={(e) => setNewVersionFile(e.target.files[0])}
              />
              {newVersionFile ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <span className="text-sm">{newVersionFile.name}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setNewVersionFile(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={handleUploadNewVersion} disabled={uploadingVersion}>
                      {uploadingVersion ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      Upload
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => versionFileRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New Version
                </Button>
              )}
            </div>

            {/* Version List */}
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {selectedResourceVersions?.versions?.slice().reverse().map((version) => (
                  <div 
                    key={version.version}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      version.version === selectedResourceVersions.current_version 
                        ? 'bg-primary/10 border border-primary/20' 
                        : 'bg-muted/50'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Version {version.version}</span>
                        {version.version === selectedResourceVersions.current_version && (
                          <Badge variant="default" className="text-xs">Current</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {version.original_filename} - {formatFileSize(version.file_size)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        By {version.uploaded_by_name} on {new Date(version.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownloadVersion(
                        selectedResourceVersions.resource_id, 
                        version.version, 
                        version.original_filename
                      )}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setVersionDialogOpen(false);
              setSelectedResourceVersions(null);
              setNewVersionFile(null);
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
